import { ExpandMore, InfoOutlined } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AppBar,
  Avatar,
  Box,
  DialogContent,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';

import { grey } from '@mui/material/colors';
import { Loader, pluralize } from '@prismamedia/one-components';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  GetArticleQuery_query_article_Article_histories_ArticleHistory,
  GetArticleQuery_query_article_Article_histories_ArticleHistory_user,
  GetArticleQuery_query_article_Article_histories_ArticleHistory_user_OneUser,
} from '../../../../../../__generated__/queries-web';

interface ResultHistoryType {
  id: string;
  title: string;
  users: {
    id: string;
    user: GetArticleQuery_query_article_Article_histories_ArticleHistory_user | null;
    createdAt: string;
  }[];
}

export const ArticleHistory = ({
  histories,
  historyCount,
  loading,
}: {
  histories: GetArticleQuery_query_article_Article_histories_ArticleHistory[];
  historyCount?: number;
  loading?: boolean;
}) => {
  const resultHistory = histories.reduce(
    (acc: ResultHistoryType[], item, index) => {
      const preventItem = histories[index - 1];

      // Create a formatted username
      const getUserName = (
        user:
          | GetArticleQuery_query_article_Article_histories_ArticleHistory_user
          | null
          | undefined,
      ): string => {
        if (!user) return 'Unknown User';
        return user.__typename === 'OneUser'
          ? (user as GetArticleQuery_query_article_Article_histories_ArticleHistory_user_OneUser)
              .name || 'Unknown User'
          : 'System';
      };

      // Group histories by creation time
      if (
        index > 0 &&
        Math.abs(
          new Date(item.createdAt).getTime() -
            new Date(preventItem.createdAt).getTime(),
        ) < 60000
      ) {
        // If created within 1 minute of each other, group them
        // eslint-disable-next-line fp/no-mutating-methods
        acc[acc.length - 1].users.push({
          id: crypto.randomUUID(),
          user: item.user || null,
          createdAt: item.createdAt,
        });
      } else {
        // Create a new history group
        // eslint-disable-next-line fp/no-mutating-methods
        acc.push({
          id: crypto.randomUUID(),
          title: `Modifié par ${getUserName(item.user)} - ${format(
            new Date(item.createdAt),
            'dd/MM/yyyy à HH:mm',
            { locale: fr },
          )}`,
          users: [
            {
              id: crypto.randomUUID(),
              user: item.user || null,
              createdAt: item.createdAt,
            },
          ],
        });
      }

      return acc;
    },
    [],
  );

  return (
    <>
      <AppBar
        sx={{
          zIndex: 2,
          '& h6': {
            marginLeft: 1,
          },
        }}
        color="inherit"
        position="static"
      >
        <Toolbar>
          <Typography variant="h6">Historique des modifications</Typography>
        </Toolbar>
      </AppBar>
      <DialogContent sx={{ backgroundColor: grey[200], maxHeight: '60vh' }}>
        <Box
          sx={{
            padding: [2, 1],
            '& table tr': {
              '& svg': {
                marginRight: 1,
                position: 'relative',
                top: 0.5,
                transform: 'rotate(-90deg)',
              },

              '& th': {
                fontSize: '1rem',
                border: 'none',
                padding: 1,

                '&:last-child': {
                  fontWeight: 500,
                },
              },
            },
          }}
        >
          {loading ? (
            <Loader />
          ) : (
            <>
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  gap: 0.5,
                  marginBottom: 3,
                }}
              >
                <InfoOutlined
                  color="primary"
                  sx={{
                    position: 'relative',
                    top: '-1px',
                  }}
                />
                <Typography>
                  {historyCount} {pluralize('modification', historyCount)}{' '}
                  {pluralize('réalisée', historyCount)} sur cet article
                </Typography>
              </Box>

              {resultHistory.map((history) => {
                return (
                  <Accordion
                    sx={{
                      '.MuiAccordionSummary-root': {
                        boxShadow: 'none',
                      },
                    }}
                    key={history.id}
                  >
                    <AccordionSummary
                      expandIcon={
                        <Tooltip
                          enterDelay={350}
                          placement="top"
                          title="Détails"
                        >
                          <ExpandMore />
                        </Tooltip>
                      }
                      sx={{
                        ...{
                          '.MuiAccordionSummary-content': {
                            '& > div': {
                              flex: '1 auto',
                              display: 'flex',
                              alignItems: 'center',

                              '& > p': {
                                marginLeft: 1.5,
                                fontWeight: 500,
                              },
                            },

                            '& > p:last-child': {
                              color: grey[500],
                              fontSize: 'typography.fontSize',
                              marginRight: 3,
                            },
                          },
                        },
                      }}
                    >
                      <Box>
                        <Typography>{history.title}</Typography>
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails
                      sx={{
                        '.MuiAccordionDetails-root': {
                          borderTop: `dashed 1px ${grey[200]}`,
                          padding: 2,
                        },
                      }}
                    >
                      {history.users.map((user) => {
                        const userName = user?.user
                          ? user.user.__typename === 'OneUser'
                            ? (user.user as GetArticleQuery_query_article_Article_histories_ArticleHistory_user_OneUser)
                                .name || 'Unknown User'
                            : 'System'
                          : 'Unknown User';

                        const avatarUrl =
                          user?.user && user.user.__typename === 'OneUser'
                            ? (user.user as GetArticleQuery_query_article_Article_histories_ArticleHistory_user_OneUser)
                                .avatarUrl
                            : undefined;

                        return (
                          <Box
                            key={user.id}
                            sx={{ display: 'flex', padding: '10px' }}
                          >
                            <Avatar
                              sx={{ width: 24, height: 24 }}
                              src={avatarUrl || undefined}
                            />
                            <Typography sx={{ marginX: '10px' }}>
                              {userName}
                            </Typography>
                            <Typography>
                              {format(
                                new Date(user.createdAt),
                                'dd/MM/yyyy à HH:mm',
                                { locale: fr },
                              )}
                            </Typography>
                          </Box>
                        );
                      })}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </>
          )}
        </Box>
      </DialogContent>
    </>
  );
};
